<template>
  <section id="news" class="rt-section news">
    <div class="rt-inner">
      <div class="news__head">
        <h2 class="rt-section-title news__title">お知らせ <span class="news__sub">News</span></h2>
      </div>

      <div v-if="messageList.length > 0" class="news__list">
        <button
          v-for="message in messageList"
          :key="message.id"
          type="button"
          class="news__row"
          @click="selectMessage(message.id)"
        >
          <span class="news__date">{{ message.publishedAt }}</span>
          <span class="news__row-title">{{ message.title }}</span>
          <i class="fas fa-chevron-right news__chevron" aria-hidden="true"></i>
        </button>
      </div>
      <p v-else class="news__empty">お知らせはありません</p>
    </div>

    <!-- 詳細モーダル -->
    <div
      v-if="showModal"
      class="news-modal"
      role="dialog"
      aria-modal="true"
      aria-label="お知らせ詳細"
      @click.self="closeModal"
      @keydown.esc="closeModal"
    >
      <div ref="panel" class="news-modal__panel" tabindex="-1">
        <div class="news-modal__header">
          <h3 class="news-modal__title">{{ message.title }}</h3>
          <button type="button" class="news-modal__close" aria-label="閉じる" @click="closeModal">
            <i class="fas fa-times" aria-hidden="true"></i>
          </button>
        </div>
        <div class="news-modal__body">
          <div class="news-modal__date">{{ message.publishedAt }}</div>
          <quill-editor
            v-model:value="message.messageBody"
            :options="quillRoOptions"
            :disabled="true"
          />
          <div v-if="message.attachmentList && message.attachmentList.length > 0" class="news-modal__attachments">
            <div class="news-modal__attachments-label">添付ファイル</div>
            <button
              v-for="(blob, index) in message.attachmentList"
              :key="blob.blobName"
              type="button"
              class="news-modal__attachment"
              @click="download(index)"
            >
              <i class="fas fa-download" aria-hidden="true"></i> {{ blob.fileName }}
            </button>
          </div>
        </div>
        <div class="news-modal__footer">
          <button type="button" class="rt-btn rt-btn--ink" @click="closeModal">閉じる</button>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import { defineComponent, nextTick, onMounted, ref } from 'vue'
import axios from 'axios'
import QuillEditor from 'vue3-quill/src/editor.vue'
import { messageListRequest, messageDetailUrl, attachmentDownloadRequest } from '../../lib/monstrsApi'

const API_BASE = process.env.VUE_APP_MONSTRS_API

export default defineComponent({
  name: 'HomeNews',
  components: { QuillEditor },
  setup () {
    const messageList = ref([])
    const message = ref({})
    const showModal = ref(false)
    const panel = ref(null)

    // 読み取り専用の Quill 設定（ツールバー無し・編集不可）
    const quillRoOptions = {
      modules: { toolbar: null },
      readOnly: true
    }

    const selectMessage = (id) => {
      message.value = {} // 前の記事の内容が一瞬見えるのを防ぐ
      showModal.value = true
      nextTick(() => panel.value && panel.value.focus())
      axios.get(messageDetailUrl(API_BASE, id)).then(response => {
        message.value = response.data
      }).catch(() => {
        message.value = {}
      })
    }

    const closeModal = () => {
      showModal.value = false
    }

    const download = async (index) => {
      try {
        const blob = message.value.attachmentList[index]
        const req = attachmentDownloadRequest(API_BASE, blob.blobName)
        const response = await axios.get(req.url, { params: req.params })
        const a = document.createElement('a')
        a.href = response.data
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)
      } catch (e) {
        // ダウンロードに失敗しても画面を壊さない
      }
    }

    onMounted(() => {
      const req = messageListRequest(API_BASE)
      axios.get(req.url, { params: req.params }).then(response => {
        messageList.value = (response.data && response.data.list) || []
      }).catch(() => {
        messageList.value = []
      })
    })

    return { messageList, message, showModal, panel, quillRoOptions, selectMessage, closeModal, download }
  }
})
</script>

<style scoped>
.news {
  background: var(--base);
}

.news__head {
  text-align: left;
  margin-bottom: 16px;
}

.news__title {
  text-align: left;
}

.news__sub {
  font-family: var(--font-lat);
  font-size: 13px;
  color: var(--ink-mute);
  margin-left: 6px;
}

.news__list {
  background: var(--surface);
  border: 1px solid var(--line);
  border-radius: var(--radius);
  overflow: hidden;
}

.news__row {
  width: 100%;
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 15px 18px;
  background: none;
  border: none;
  border-bottom: 1px solid var(--line);
  cursor: pointer;
  text-align: left;
  font-family: var(--font-head);
  transition: background 0.15s;
}

.news__row:last-child {
  border-bottom: none;
}

.news__row:hover {
  background: var(--blue-bg);
}

.news__date {
  font-family: var(--font-lat);
  font-size: 12px;
  color: var(--ink-mute);
  white-space: nowrap;
}

.news__row-title {
  flex: 1;
  font-size: 13.5px;
  color: var(--ink);
}

.news__chevron {
  font-size: 12px;
  color: var(--brand-blue);
}

.news__empty {
  background: var(--surface);
  border: 1px solid var(--line);
  border-radius: var(--radius);
  padding: 28px;
  text-align: center;
  color: var(--ink-mute);
  font-size: 14px;
}

/* モーダル */
.news-modal {
  position: fixed;
  inset: 0;
  z-index: 100;
  background: rgba(43, 43, 51, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
}

.news-modal__panel {
  background: var(--surface);
  border-radius: var(--radius-lg);
  width: 100%;
  max-width: 760px;
  max-height: 88vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.news-modal__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 18px 22px;
  border-bottom: 1px solid var(--line);
}

.news-modal__title {
  font-size: 17px;
  font-weight: 700;
  color: var(--ink);
  text-align: left;
}

.news-modal__close {
  background: none;
  border: none;
  font-size: 18px;
  color: var(--ink-mute);
  cursor: pointer;
}

.news-modal__body {
  padding: 18px 22px;
  overflow-y: auto;
  text-align: left;
}

.news-modal__date {
  font-family: var(--font-lat);
  font-size: 12px;
  color: var(--ink-mute);
  margin-bottom: 10px;
}

.news-modal__attachments {
  margin-top: 16px;
}

.news-modal__attachments-label {
  font-size: 12px;
  color: var(--ink-mute);
  margin-bottom: 8px;
}

.news-modal__attachment {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-family: var(--font-head);
  font-size: 13px;
  color: var(--brand-blue);
  background: var(--blue-bg);
  border: none;
  border-radius: var(--radius-pill);
  padding: 8px 16px;
  margin: 0 8px 8px 0;
  cursor: pointer;
}

.news-modal__footer {
  padding: 14px 22px;
  border-top: 1px solid var(--line);
  text-align: right;
}
</style>
